// @bindery/auth-consumer — the broker-consumer auth stack every sara.ai
// property's Worker composes, parameterised by one `ConsumerAppConfig`.
//
// `createAuthConsumer(app)` is the whole worker-side surface: the `/auth/*`
// routes, the Bearer gate for the app's own API, the cookie kit, and the
// config resolver. The browser half is `@bindery/auth-consumer/browser` and
// takes no config at all — it only ever calls same-origin worker routes.
//
// What stays in the app: the Worker entry (`main`), its `wrangler.jsonc` routes
// and `vars`, and any non-auth route. What comes from the topology, never from
// here or from the app by hand: `hosts` (`hostsOf(app)`), `defaultHost`
// (`canonicalHost(app, target)`) and `defaultPeers` (`peersFor(target)` /
// `SOURCE_PEERS`).

import { createAuthRoutes } from "./auth";
import {
  basePathOf,
  defaultOriginFor,
  homePathOf,
  isAuthHostFor,
  lanesOf,
  resolveConfigFor,
  type AuthConfig,
  type AuthEnv,
  type ConsumerAppConfig,
} from "./config";
import { createCookieKit, type CookieKit } from "./cookies";
import { createLogger, type AuthLogger } from "./log";
import { createReturnToGuard, type ReturnToGuard } from "./return-to";
import { requireSessionFor, type SessionResult } from "./session";
import type { SessionLane } from "./verify";

export interface AuthConsumer<L extends SessionLane = "user_session"> {
  /**
   * Every path under `basePath` (`/auth` unless the app set one):
   * `/sign-in`, `/callback`, `/token`, `/sign-out`, and with `readerLinking`
   * also `/sign-in-light` and `/manage`. Anything else under the prefix is a
   * 404 — the entry must hand this route group over whole rather than
   * cherry-pick paths.
   */
  handleAuth: (request: Request, url: URL, env: AuthEnv) => Promise<Response>;
  /**
   * The Bearer gate for the app's own API routes. Never throws. On success
   * `lane` says which session type verified — always `user_session` unless
   * the app declared `lanes`.
   */
  requireSession: (request: Request, env: AuthEnv) => Promise<SessionResult<L>>;
  /** The session types this consumer accepts, defaulted and checked. */
  readonly lanes: readonly L[];
  /**
   * The effective broker config for a request on `hostname`. Throws
   * `AuthConfigError` when the `AUTH_*` vars name half a peer set —
   * `handleAuth` and `requireSession` already turn that into a refusal, so an
   * app only needs to handle it if it calls this itself.
   */
  resolveConfig: (env: AuthEnv, hostname: string) => AuthConfig;
  /**
   * Whether this consumer serves auth on `host`. Fail-closed: the dev hosts
   * answer `false` unless the caller passes `secure: false`, because
   * `localhost` is a name an attacker can put in a `Host` header.
   */
  isAuthHost: (host: string, secure?: boolean) => boolean;
  /** The cookie builders and readers, closed over this app's cookie names. */
  readonly cookies: CookieKit;
  /** Structured logging bound to this app's `service` discriminator. */
  readonly log: AuthLogger;
  /** The `returnTo` guard, closed over this app's host allowlist. */
  readonly returnTo: ReturnToGuard;
  /** Where a request on a host we don't serve auth on is funnelled. */
  readonly defaultOrigin: string;
  /**
   * What the app's `wrangler.jsonc` `assets.run_worker_first` must contain for
   * the `${basePath}/*` group to be reachable at all. An app with its own worker
   * routes appends to this; its `scripts/wrangler.test.ts` is what keeps the
   * two from drifting.
   */
  readonly RUN_WORKER_FIRST: readonly [string];
  /** The prefix every route above sits under and the cookies' `Path`. */
  readonly basePath: string;
}

/** Build one app's auth consumer. Call once at module scope in the Worker. */
export function createAuthConsumer<L extends SessionLane = "user_session">(
  app: ConsumerAppConfig<L>,
): AuthConsumer<L> {
  // Checked first, so a consumer declaring no lane (or an unknown one) fails
  // at module scope — the deploy's first request — rather than refusing every
  // sign-in as `wrong_typ`. `lanesOf` returns `app.lanes` itself when set and
  // the user-only default otherwise, which is `L`'s default.
  const lanes = lanesOf(app) as readonly L[];
  const basePath = basePathOf(app);
  const homePath = homePathOf(app);
  const cookies = createCookieKit(app.cookiePrefix, basePath);
  // The absolute-`returnTo` allowlist IS the host list: a host we hand a
  // session cookie to is a host we can hand a redirect to. Derived rather than
  // restated, so the two cannot drift.
  const returnTo = createReturnToGuard(app.hosts, homePath);
  const log = createLogger(app.service);
  const { handleAuth } = createAuthRoutes({
    app,
    cookies,
    returnTo,
    log,
    basePath,
    homePath,
  });

  return {
    handleAuth,
    requireSession: (request, env) => requireSessionFor(app, request, env),
    lanes,
    resolveConfig: (env, hostname) => resolveConfigFor(app, env, hostname),
    isAuthHost: (host, secure) => isAuthHostFor(app, host, secure),
    cookies,
    log,
    returnTo,
    defaultOrigin: defaultOriginFor(app),
    RUN_WORKER_FIRST: [`${basePath}/*`],
    basePath,
  };
}

export {
  AuthConfigError,
  COOKIE_PATH,
  DEFAULT_BASE_PATH,
  DEFAULT_DEV_HOSTS,
  DEFAULT_HOME_PATH,
  MAX_SESSION_SECONDS,
  MAX_TRIES,
  STATE_BYTES,
  STATE_TTL_SECONDS,
  TRY_TTL_SECONDS,
  exchangeAudienceFor,
} from "./config";
export type {
  AnyConsumerAppConfig,
  AuthConfig,
  AuthEnv,
  ConsumerAppConfig,
} from "./config";
export { cookieNamesFor } from "./cookies";
export type { CookieKit, CookieNames, StateCheck } from "./cookies";
export { EXCHANGE_TIMEOUT_MS, exchangeCode } from "./exchange";
export type { ExchangeParams, ExchangeResult } from "./exchange";
export type { AuthLogData, AuthLogger, LogLevel } from "./log";
export { createReturnToGuard } from "./return-to";
export type { ReturnToGuard } from "./return-to";
export {
  FOUNDER_ROLE,
  capabilityVerdict,
  founderVerdict,
  roleVerdict,
} from "./role-gate";
export type { Capabilities, GatedSession, RoleVerdict } from "./role-gate";
export type { SessionResult } from "./session";
export {
  DEFAULT_LANES,
  JWKS_TIMEOUT_MS,
  SESSION_LANES,
  classifyJoseError,
  getRemoteJwks,
  isSessionLane,
  verifyBrokerJwt,
} from "./verify";
export type {
  ReaderSessionClaims,
  SessionClaims,
  SessionLane,
  VerifiedSession,
  VerifyKey,
  VerifyOptions,
  VerifyResult,
} from "./verify";
